'use strict'

/**
 * CXICCProfile
 * Workflow:
 * - Store ICC profile metadata (type, name, description, path).
 * - Provide typed enums that mirror Android constants.
 */
class CXICCProfile {
    /**
     * @param {Object|Object} profileTypeOrInternal
     * @param {string} [profileFilePath]
     * @param {string} [profileName]
     * @param {string} [profileDescription]
     */
    constructor(profileTypeOrInternal, profileFilePath, profileName, profileDescription) {
        if (profileTypeOrInternal && profileTypeOrInternal.__isInternalProfile) {
            const internal = profileTypeOrInternal
            this.profileType = internal.getProfileType()
            this.profileName = internal.getName()
            this.profileDescription = internal.getDescription()
            this.profileFilePath = internal.getPath()
            return
        }

        this.profileType = profileTypeOrInternal
        this.profileFilePath = profileFilePath || ''
        this.profileName = profileName || ''
        this.profileDescription = profileDescription || ''
    }

    /**
     * @returns {Object}
     */
    getProfileType() {
        return this.profileType
    }

    /**
     * @returns {string}
     */
    getProfileFilePath() {
        return this.profileFilePath
    }

    /**
     * @returns {string}
     */
    getProfileName() {
        return this.profileName
    }

    /**
     * @returns {string}
     */
    getProfileDescription() {
        return this.profileDescription
    }
}

CXICCProfile.INSTALL_ICC_FOLDER = 'icc/'

const createProfileType = (value, name) =>
    Object.freeze({
        value,
        name,
        getValue() {
            return this.value
        },
        getName() {
            return this.name
        },
    })

// Enum-like list of profile types.
CXICCProfile.ICCProfileType = Object.freeze({
    RGB: createProfileType(0, 'RGB'),
    CMYK: createProfileType(1, 'CMYK'),
    Lab: createProfileType(2, 'Lab'),
})

const createInternalProfile = (value, profileType, name, description, path) =>
    Object.freeze({
        __isInternalProfile: true,
        value,
        profileType,
        name,
        description,
        path,
        getValue() {
            return this.value
        },
        getProfileType() {
            return this.profileType
        },
        getName() {
            return this.name
        },
        getDescription() {
            return this.description
        },
        getPath() {
            return this.path
        },
    })

// Enum-like list of internal ICC profiles.
CXICCProfile.InternalICCProfile = Object.freeze({
    sRGB: createInternalProfile(
        0,
        CXICCProfile.ICCProfileType.RGB,
        'sRGB',
        'sRGB IEC61966-2.1',
        'sRGB',
    ),
    CIELabD50: createInternalProfile(
        1,
        CXICCProfile.ICCProfileType.Lab,
        'Cie L*a*b D50',
        'CIE L*a*b D50',
        'CieLabD50',
    ),
})

// Default profiles (mirror Android statics).
CXICCProfile.sRGB = new CXICCProfile(CXICCProfile.InternalICCProfile.sRGB)
CXICCProfile.cieLabD50 = new CXICCProfile(CXICCProfile.InternalICCProfile.CIELabD50)

export default CXICCProfile
